"use client";

import { useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Lightbulb, Menu, X } from "lucide-react";
import { AccountControl } from "@/components/account/account-control";
import { useStudioEngagement, useStudioView } from "@/contexts/studio-context";
import { useWhatsNew } from "@/contexts/whats-new-context";
import { useBreakpoint } from "@/hooks/use-breakpoint";
import { useShortViewport } from "@/hooks/use-short-viewport";
import { AppHeaderNavSheet } from "./app-header-nav-sheet";
import { BetaBadge } from "./beta-badge";
import { getNavModel, getNavState, navLabel, type NavItem } from "./app-header-nav";

// ─── App-level navigation (V3 spec, section 02 row 1) ──────────────────
//
// Three zones in a single row:
//   1. Brand       , flex: 1 0 auto (left)
//   2. Main tabs   , flex: 0 0 auto, centered between zones 1 & 3
//   3. Utilities   , flex: 1 0 auto, justified end (mic + settings)
//
// Symmetric flex:1 on zones 1 & 3 keeps the central nav visually centered
// without a `position: absolute` hack. See spec §02, Anatomie.
//
// Cette mise en page ne résiste pas à la compression, et c'est la raison du
// repli en sheet plus bas. Les six entrées à plat faisaient 459 px, ce qui
// posait le plancher du header à 763 px pour un repli à 768 : 5 px de marge.
// L'imbrication des sous-vues du Studio ramène la nav à 425 px et le plancher
// à 729, donc 39 px de marge. Ce n'est pas un confort à dépenser : c'est ce
// qui séparait la tablette en portrait d'une barre où « Apprendre » et les
// utilitaires sortaient de l'écran, sans retour possible puisque `body` est en
// `overflow:hidden`.
//
// Ils demandent aussi de la hauteur : 50 px d'onglet ne tiennent pas dans une
// barre comprimée. La condition d'affichage est donc une seule requête,
// `(min-width:768px) and (not (max-height:480px))`, écrite telle quelle plutôt
// qu'en empilant `md:` et une variante de hauteur — l'ordre de deux règles de
// même spécificité ne doit pas décider de ce qui est visible.
//
// `not (max-height:480px)` et non `min-height:481px` : ce doit être le
// complément **exact** de `useShortViewport`, qui teste `max-height: 480px`.
// Avec 481, toute hauteur fractionnaire entre les deux — le zoom navigateur et
// la mise à l'échelle système en produisent — ne satisfaisait ni l'une ni
// l'autre : la nav en ligne restait masquée, le bouton menu aussi, et plus
// aucune entrée de navigation n'était atteignable depuis le header.
//
// Le basculement est piloté par le CSS et non par les hooks : `useBreakpoint`
// et `useShortViewport` annoncent tous deux la version mobile au premier
// rendu, un poste desktop la verrait passer. Le CSS, lui, est juste dès la
// première peinture. Les hooks ne servent qu'à décider si le sheet est monté,
// et comme celui-ci démarre fermé, la valeur du premier rendu ne peut rien
// fausser.

/** L'anneau de focus de la barre. `--color-accent` et non
 *  `--color-violet-glow` : ce dernier est un violet à 35 % d'alpha, soit
 *  1,5:1 sur le fond profond, donc un focus clavier qu'on ne voit pas. */
const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

export function AppHeader() {
  const pathname = usePathname();
  const router = useRouter();
  const [, setView] = useStudioView();
  const [engagement, setEngagement] = useStudioEngagement();
  const { openModal: openWhatsNew, hasUnread } = useWhatsNew();
  const breakpoint = useBreakpoint();
  const shortViewport = useShortViewport();
  const [menuOpen, setMenuOpen] = useState(false);
  const engaged = engagement !== null;
  const inlineModel = getNavModel(engaged, "inline");
  const sheetModel = getNavModel(engaged, "sheet");
  const navState = getNavState(pathname, engaged);
  const activeLabel = navLabel(navState.page);

  // Le sheet n'existe que là où la nav est repliée : ailleurs, le CSS le
  // masquerait mais il resterait monté, à piéger le focus et à intercepter
  // Échap pour un panneau que personne ne voit. On le dérive de l'écran plutôt
  // que de le refermer dans un effet — un état de moins à resynchroniser.
  const navFolded = breakpoint === "compact" || shortViewport;
  const sheetOpen = menuOpen && navFolded;

  // Dériver `sheetOpen` suffit à cacher le sheet quand la nav se déplie, mais
  // `menuOpen` resterait à `true` : en repassant à une largeur repliée, le
  // sheet se rouvrirait de lui-même, et `useModalShell` volerait le focus pour
  // un panneau que personne n'a demandé. On remet à plat pendant le rendu
  // plutôt que dans un effet — React relance aussitôt, donc aucune frame
  // intermédiaire n'est peinte, et il n'y a pas d'effet à resynchroniser.
  if (!navFolded && menuOpen) {
    setMenuOpen(false);
  }

  const handleNavClick = (item: NavItem) => {
    // Quitter un parcours par un onglet relâche l'engagement, sinon le studio
    // continue de supprimer tous les overlays (Harmonie / Accords / …) et la
    // vue demandée rend une page blanche. Choisir un instrument d'inspection,
    // c'est sortir de ce qu'on travaillait.
    if (engagement !== null) {
      setEngagement(null);
    }

    // Selecting a studio view *is* the navigation: `setView` pushes the
    // matching route (`/studio`, `/studio/harmony`, …).
    if (item.view !== null) {
      setView(item.view);

      return;
    }

    if (item.href !== null && pathname !== item.href) {
      router.push(item.href);
    }
  };

  return (
    <header
      /* `relative` ancre le sheet sous la barre.

         La compression verticale vise le téléphone couché (667×375), où
         48 px de header + 56 px de toolbar mangeaient 28 % de la hauteur. Le
         seuil est en hauteur seule : un grand téléphone en paysage fait plus
         de 768 px de large et échapperait à un critère de largeur.

         `z-45` et non `z-30` : le header est une barre statique, mais son
         menu déplié descend sur la page et doit passer au-dessus des calques
         plein écran qui plafonnent à `z-40` — au premier rang celui qui
         demande de tourner l'appareil, et qui compte justement sur le header
         comme seule sortie. Les modales restent au-dessus (`z-50`). */
      className="relative flex shrink-0 items-center h-12 [@media(max-height:480px)]:h-10 px-[18px] bg-bg-deep select-none z-45"
      style={{ borderBottom: "1px solid var(--color-border)" }}
    >
      {/* Zone 1, Brand. flex:1 keeps the central nav centered. */}
      <div className="flex-1 flex items-center min-w-0 gap-2">
        <span
          className="shrink-0 text-[14px] font-semibold tracking-tight"
          style={{ color: "var(--color-text-primary)" }}
        >
          <span
            className="font-[Inter] text-[13px] font-semibold"
            style={{ color: "var(--color-accent)" }}
          >
            V
          </span>
          irtuosa
        </span>

        {/* L'état du produit, détaché du mot-marque : la pastille dit dans quel
            état est Virtuosa, elle ne le rebaptise pas « Virtuosa Bêta ». C'est
            aussi ce qui la rend retirable en une ligne le jour de la sortie de
            bêta. Voir `beta-badge.tsx` pour ce que cette zone coûte, et pour
            les autres emplacements écartés. */}
        <BetaBadge focusRing={FOCUS_RING} />

        {/* Sans la nav en ligne, la barre ne dit plus où l'on se trouve : le
            libellé de la vue courante reprend ce rôle. */}
        {activeLabel && (
          <span
            className="truncate [@media(min-width:768px)_and_(not_(max-height:480px))]:hidden text-[12px] tracking-tight"
            style={{ color: "var(--color-text-secondary)" }}
          >
            <span aria-hidden style={{ color: "var(--color-text-muted)" }}>
              /{" "}
            </span>
            {activeLabel}
          </span>
        )}
      </div>

      {/* Zone 2, Main tabs. Pure inline layout, no centering hack.

          L'imbrication est portée par des `ul`/`li` et pas seulement par la
          typographie : le retrait visuel ne dit rien à un lecteur d'écran, la
          structure si. C'est aussi ce qui interdit d'écrire la hiérarchie
          deux fois, ici et dans le sheet — elle vient du modèle.

          Le rythme des écarts fait le reste du travail : 24 px entre
          destinations, 10 px entre le Studio et son groupe. C'est ce rapport,
          plus que la pilule, qui dit lesquelles de ces entrées vont
          ensemble. */}
      <nav
        aria-label="Navigation principale"
        className="hidden [@media(min-width:768px)_and_(not_(max-height:480px))]:flex items-center shrink-0"
      >
        <ul className="flex items-center gap-6">
          {inlineModel.nodes.map((node) => {
            const current = node.key === navState.page;
            // Le troisième état : le Studio est sur le chemin sans être la
            // destination. Il s'allume, mais garde le soulignement pour la
            // page courante — deux marqueurs identiques ne hiérarchisent rien.
            const parent =
              node.key === "studio" && navState.studioActive && !current;
            const childCurrent = node.children.some(
              (child) => child.key === navState.page,
            );

            return (
              <li key={node.key} className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={() => handleNavClick(node)}
                  aria-current={current ? "page" : parent ? "true" : undefined}
                  className={`relative cursor-pointer text-[13px] tracking-tight transition-colors duration-150 py-[15px] rounded-sm ${FOCUS_RING}`}
                  style={{
                    color: current
                      ? "var(--color-text-primary)"
                      : parent
                        ? "var(--color-text-secondary)"
                        : "var(--color-text-soft)",
                    fontWeight: current ? 600 : parent ? 500 : 400,
                  }}
                >
                  {node.label}
                  {current && (
                    <span
                      aria-hidden="true"
                      className="absolute left-0 right-0 h-[2px] rounded-full"
                      style={{
                        bottom: "-1px",
                        background: "var(--color-accent)",
                      }}
                    />
                  )}
                </button>

                {node.children.length > 0 && (
                  <ul
                    className="flex items-center gap-3 h-[26px] px-2 rounded-lg transition-colors duration-150"
                    style={{
                      // La pilule entière prend l'accent quand une sous-vue
                      // est courante : le fond porte le marqueur, et le
                      // libellé n'a donc pas de soulignement à ajouter.
                      background: childCurrent
                        ? "var(--color-accent-subtle)"
                        : "var(--color-surface-card)",
                      border: `1px solid ${
                        childCurrent
                          ? "var(--color-border-strong)"
                          : "var(--color-border-subtle)"
                      }`,
                    }}
                  >
                    {node.children.map((child) => {
                      const active = child.key === navState.page;

                      return (
                        <li key={child.key} className="flex">
                          <button
                            type="button"
                            onClick={() => handleNavClick(child)}
                            aria-current={active ? "page" : undefined}
                            /* La pilule fait 26 px de haut et le libellé 17 :
                               en dessous des 24 px de WCAG 2.5.8. La zone de
                               contact est étendue à la hauteur de barre par un
                               pseudo-élément, comme le bouton de tempo de la
                               toolbar. C'est la zone qui dépasse le visuel, et
                               pas l'inverse : grandir la pilule reprendrait la
                               largeur que l'imbrication vient de rendre. */
                            className={`relative cursor-pointer text-[12px] leading-[1.4] tracking-tight transition-colors duration-150 rounded-sm after:absolute after:content-[''] after:-inset-y-[15px] after:inset-x-0 ${FOCUS_RING}`}
                            style={{
                              color: active
                                ? "var(--color-text-primary)"
                                : "var(--color-text-soft)",
                              fontWeight: active ? 600 : 400,
                            }}
                          >
                            {child.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Zone 3, Mirror of zone 1 (flex:1) so the central nav stays
          visually centered without a `position: absolute` hack. Mic and
          settings utilities live in the Toolbar, see spec §02. The
          "Nouveautés & feedback" launcher is the one transverse utility we
          keep here so it's reachable from every route.
          `min-w-0` : sans lui la zone refuse de se comprimer et pousse le
          reste hors de l'écran. Compte et Nouveautés restent dans la barre
          même en compact — une pastille de non-lu derrière un menu ne
          notifie plus personne, et le retour de l'inscrit ne doit pas coûter
          deux gestes. */}
      <div className="flex-1 min-w-0 flex items-center justify-end gap-2">
        <AccountControl />
        <button
          type="button"
          /* Enveloppé, et pas passé directement : `openModal` prend
             désormais un onglet, et `onClick={openWhatsNew}` lui livrerait
             l'événement de clic à sa place. */
          onClick={() => openWhatsNew()}
          aria-label="Nouveautés et feedback"
          title="Nouveautés"
          className={`relative shrink-0 w-8 h-8 grid place-items-center rounded-lg text-accent-bright hover:text-white hover:bg-accent-subtle transition-colors duration-150 cursor-pointer ${FOCUS_RING}`}
          style={{
            filter:
              "drop-shadow(0 0 4px rgba(167, 139, 250, 0.9)) drop-shadow(0 0 9px rgba(139, 92, 246, 0.7))",
          }}
        >
          <Lightbulb size={16} strokeWidth={2} />
          {hasUnread && (
            <span
              aria-hidden="true"
              className="absolute top-1 right-1 w-[7px] h-[7px] rounded-full"
              style={{
                background: "var(--color-accent)",
                boxShadow: "0 0 6px var(--color-violet-glow)",
              }}
            />
          )}
        </button>

        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-label={sheetOpen ? "Fermer le menu" : "Ouvrir le menu"}
          aria-expanded={sheetOpen}
          aria-haspopup="dialog"
          className={`[@media(min-width:768px)_and_(not_(max-height:480px))]:hidden shrink-0 w-8 h-8 grid place-items-center rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors duration-150 cursor-pointer ${FOCUS_RING}`}
        >
          {sheetOpen ? (
            <X size={18} strokeWidth={2} />
          ) : (
            <Menu size={18} strokeWidth={2} />
          )}
        </button>
      </div>

      <AppHeaderNavSheet
        open={sheetOpen}
        onClose={() => setMenuOpen(false)}
        model={sheetModel}
        state={navState}
        onSelect={handleNavClick}
      />
    </header>
  );
}
